import { ReactElement } from 'react';

import { Endpoint, Report, ReportService } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { ModalService } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';

import { ConsultaAlteracaoProdutoAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import { getLoteStampBySituacao, LoteSituacao } from '../lote/lote-utils';
import './consultar-alteracao-produto-controller.css';

enum SituacaoAlteracao {
  APROVADO = 'A',
  REPROVADO = 'R',
  PENDENTE = 'P',
}

interface Data {
  dsSituacao: string;
  ieSituacao: SituacaoAlteracao;
  ieObservacao: string;
  nrSeqSolicAlt: number;
  nrSeqBeneficiario: number;
}

const OptionKey = {
  IMPRIMIR: 'imprimir',
  OBSERVACAO: 'observacao',
};

@Controller({ fragment: FragmentNames.CONSULTA_ALTERACAO_PRODUTO })
class ConsultarAlteracaoProdutoController extends BaseController {

  private reportService: ReportService;

  private exibir: boolean;

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
    void this.exibirImprimir().then((resp: boolean) => this.exibir = resp);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { dsSituacao, ieSituacao } = data as Data;

    const situacaoMap = {
      [SituacaoAlteracao.REPROVADO]: LoteSituacao.REJEITADA_PELA_OPERADORA,
      [SituacaoAlteracao.PENDENTE]: LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO,
      [SituacaoAlteracao.APROVADO]: LoteSituacao.INTEGRADO_A_PROPOSTA,
    };

    const content = (): ReactElement => (getLoteStampBySituacao(situacaoMap[ieSituacao], dsSituacao));

    render(content);
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA)
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieObservacao } = data as unknown as Data;

    const show = {
      [OptionKey.IMPRIMIR]: this.exibir,
      [OptionKey.OBSERVACAO]: ieObservacao == 'S',
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { key } = event;
    const { nrSeqSolicAlt, nrSeqBeneficiario } = event.data as unknown as Data;

    const action = {
      [OptionKey.OBSERVACAO]: () => this.modalObservacao(nrSeqSolicAlt),
      [OptionKey.IMPRIMIR]: () => this.imprimir(nrSeqSolicAlt, nrSeqBeneficiario),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private modalObservacao(nrSeqSolicAlt: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'observacao-alteracao-produto',
      title: 'beneficiario.observacao',
      contextData: { nrSeqContrato, nrSeqSolicAlt },
      className: 'c-consultar-alteracao-produto-controller__custom-width-wrap-modal',
    };

    ModalService.openDefault(modalWithFragment);
  }

  private imprimir(nrSequencia: number, nrSeqBeneficiario: number): void {
    this.reportService = new ReportService('23', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQUENCIA: nrSequencia, NR_SEQ_SEGURADO: nrSeqBeneficiario });
  }

  private exibirImprimir = async (): Promise<boolean> => {
    const service = new ReportService('23', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    const exibe = relatorios.length === 0 ? false : true;
    return exibe;
  };

}
export default ConsultarAlteracaoProdutoController;
